import React from 'react'
import { useLocation } from 'wouter'
import { useCalendarWeeks } from '../lib/data-service'
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext'
import { useTenant } from '../contexts/TenantContext'
import { RetailerSelector } from './RetailerSelector'
import { Breadcrumbs, getBreadcrumbsForPath } from './Breadcrumbs'
import { CalendarWeekPicker } from './CalendarWeekPicker'
import { Calendar, RefreshCw } from 'lucide-react'
import { getRetailerInfo } from '../config/retailers'

interface GlobalHeaderProps {
  title: string
  subtitle: string
  onRefresh?: () => void
  isLoading?: boolean
}

export default function GlobalHeader({ title, subtitle, onRefresh, isLoading }: GlobalHeaderProps) {
  const { selectedDateRange, setSelectedDateRange, setSelectedFiscalWeek } = useFiscalWeekContext()
  const { data: calendarData } = useCalendarWeeks()
  const calendarWeeks = calendarData?.weeks
  const { selectedRetailer, selectedRetailerId } = useTenant()
  const [location] = useLocation()
  const [isScrolled, setIsScrolled] = React.useState(false)

  // Get retailer branding info
  const retailerInfo = selectedRetailerId ? getRetailerInfo(selectedRetailerId) : null

  // Auto-select the current (most recent completed) week on load
  React.useEffect(() => {
    if (!selectedDateRange && calendarData && calendarData.weeks.length > 0) {
      const current = calendarData.weeks.find(w => w.fiscal_week === calendarData.currentWeek && w.fiscal_year === calendarData.currentFiscalYear)
      const week = current ?? calendarData.weeks[0]
      setSelectedDateRange({ start: week.start_date, end: week.end_date })
      const fw = parseInt(week.fiscal_year.replace('FY', '')) * 100 + week.fiscal_week
      setSelectedFiscalWeek(fw)
    }
  }, [calendarData, selectedDateRange, setSelectedDateRange, setSelectedFiscalWeek])

  // Handle scroll for sticky shadow effect
  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Get breadcrumbs for current location
  const breadcrumbItems = getBreadcrumbsForPath(location)

  return (
    <div className="sticky top-0 z-40">
      {/* Subtle retailer accent bar (only when retailer selected) */}
      {retailerInfo && (
        <div
          className="h-1 w-full"
          style={{ backgroundColor: retailerInfo.colors.primary }}
        />
      )}

      <div className={`bg-background/95 backdrop-blur-md transition-all duration-200 ${isScrolled ? 'shadow-md border-b border-border/60' : ''}`}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-4">
        <div className="space-y-4">
          {/* Breadcrumbs */}
          <Breadcrumbs items={breadcrumbItems} />

          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl">
                <svg className="w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18" />
                  <path d="M18 17V9" />
                  <path d="M13 17V5" />
                  <path d="M8 17v-3" />
                </svg>
              </div>
              <div>
                <div className="flex items-center space-x-3">
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-blue-800 bg-clip-text text-transparent tracking-tight">
                    {title}
                  </h1>

                  {/* Retailer logo and name (when filtered) */}
                  {retailerInfo && (
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-400">&bull;</span>
                      {retailerInfo.logoPath ? (
                        <img
                          src={retailerInfo.logoPath}
                          alt={retailerInfo.name}
                          className="h-6 w-auto object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <span
                          className="text-base font-semibold"
                          style={{ color: retailerInfo.colors.primary }}
                        >
                          {selectedRetailer?.name}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{subtitle}</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Retailer Selector */}
              <RetailerSelector showLabel={false} />

              {/* Calendar Week Picker */}
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                {calendarWeeks && calendarWeeks.length > 0 && (
                  <CalendarWeekPicker
                    weeks={calendarWeeks}
                    selectedRange={selectedDateRange}
                    onSelectRange={setSelectedDateRange}
                    onSelectFiscalWeek={setSelectedFiscalWeek}
                  />
                )}
              </div>

        {/* Refresh Button */}
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="p-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        )}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  )
}
